import { type Time as TimeValue, parseTime } from '@internationalized/date';
import React, { type ReactNode, useCallback, useMemo, useRef } from 'react';
import {
  Select as SelectRAC,
  Button as ButtonRAC,
  SelectValue as SelectValueRAC,
  Popover as PopoverRAC,
  ListBox as ListBoxRAC,
  ListBoxItem as ListBoxItemRAC,
  TextField as TextFieldRAC,
  Input as InputRAC,
  TimeField as TimeFieldRAC,
  DateInput as DateInputRAC,
  DateSegment as DateSegmentRAC,
  type DateSegmentProps,
} from 'react-aria-components';

import { useConfig } from '../../contexts/config-context';
import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { useTranslation } from '../../contexts/translation-context';
import { findChildOfType, applyInternalProps } from '../../services/find-child';
import {
  getDateFormatParts,
  isSupportedDatePart,
  SupportedDateParts,
} from '../../utils/date-format-parts';
import { deserializeDate, serializeDate } from '../date-picker/helpers';
import { getMonthList } from '../date-picker/month-select/helpers';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import {
  Label,
  type LabelInternalProps,
} from '../field-label/field-label-headless';
import { FieldsetHeadless } from '../fieldset/fieldset-headless';

import { type DateTimeFieldProps } from './date-time-field-props';
import {
  parseDateTimeValue,
  formatDateTimeValue,
  getDateTimePartsErrors,
} from './date-time-field-utils';

type DateSegment = DateSegmentProps['segment'];

export type DateTimeInputProps = {
  children: ReactNode;
};

export const DateTimeInput = ({ children }: DateTimeInputProps) => {
  const { id, required, onBlur, onFocus, FieldLayout } =
    useFieldPropsV2<DateTimeFieldProps>();

  const { inputId, labelId, ariaDescribedBy } = useFieldAttributes();
  const { regionalFormat } = useConfig();

  const dateFormat = useMemo(
    () =>
      getDateFormatParts(regionalFormat)
        .filter(isSupportedDatePart)
        .map((part) => part.toUpperCase() as SupportedDateParts),
    [regionalFormat],
  );

  const labelElement = findChildOfType(children, DateTimeInput.Label);
  const descriptionElement = findChildOfType(
    children,
    DateTimeInput.Description,
  );
  const errorElement = findChildOfType(children, DateTimeInput.Error);

  const dateElementsByPart = {
    [SupportedDateParts.MONTH]: findChildOfType(children, DateTimeInput.Month),
    [SupportedDateParts.DAY]: findChildOfType(children, DateTimeInput.Day),
    [SupportedDateParts.YEAR]: findChildOfType(children, DateTimeInput.Year),
  };

  const timeElement = findChildOfType(children, DateTimeInput.Time);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() =>
        applyInternalProps<LabelInternalProps>(labelElement, { as: 'div' })
      }
      renderInput={() => (
        <FieldsetHeadless
          id={inputId}
          ariaLabelledBy={labelId}
          ariaDescribedBy={ariaDescribedBy}
          required={required}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {dateElementsByPart[dateFormat[0]]}
          {dateElementsByPart[dateFormat[1]]}
          {dateElementsByPart[dateFormat[2]]}
          {timeElement}
        </FieldsetHeadless>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type MonthProps = {
  children: ReactNode;
  className?: string;
};

const Month = ({ children, className }: MonthProps) => {
  const { id, disabled, showDateLabels } =
    useFieldPropsV2<DateTimeFieldProps>();
  const { inputId } = useDateTimeFieldAttributes(SupportedDateParts.MONTH);
  const { partValue, partError, onPartChange, isFirstPart } = useFieldPart(
    SupportedDateParts.MONTH,
  );
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleMonthChange = useCallback(
    (selectedValue: React.Key | null) => {
      onPartChange(typeof selectedValue === 'string' ? selectedValue : '');
    },
    [onPartChange],
  );

  useFocusFieldEvent(() => {
    if (isFirstPart) {
      buttonRef.current?.focus();
    }
  }, id);

  const labelElement = findChildOfType(children, Month.Label);
  const optionsElement = findChildOfType(children, Month.Options);

  return (
    <div className={className} data-date-field={true}>
      {showDateLabels ? labelElement : null}
      <SelectRAC
        value={partValue || null}
        onChange={handleMonthChange}
        isDisabled={disabled}
        isInvalid={partError}
      >
        <ButtonRAC
          ref={buttonRef}
          id={inputId}
          data-hook={SupportedDateParts.MONTH}
          data-invalid={partError}
        >
          <SelectValueRAC />
        </ButtonRAC>
        {optionsElement}
      </SelectRAC>
    </div>
  );
};

type MonthLabelProps = {
  className?: string;
};

const MonthLabel = ({ className }: MonthLabelProps) => {
  return (
    <DateTimeLabel datePart={SupportedDateParts.MONTH} className={className} />
  );
};

type MonthOptionsProps = {
  children: ReactNode;
  className?: string;
};

const MonthOptions = ({ children, className }: MonthOptionsProps) => {
  const { language } = useConfig();

  const months = useMemo(() => getMonthList(language), [language]);

  const optionElement = findChildOfType(children, MonthOptions.Option) ?? (
    <MonthOptions.Option />
  );

  return (
    <PopoverRAC className={className}>
      <ListBoxRAC>
        {months.map((month, index) =>
          applyInternalProps<MonthOptionInternalProps>(optionElement, {
            key: String(index + 1),
            value: String(index + 1),
            label: month,
          }),
        )}
      </ListBoxRAC>
    </PopoverRAC>
  );
};

type MonthOptionProps = {
  className?: string;
};

type MonthOptionInternalProps = {
  value: string;
  label: string;
};

const MonthOption = ({ className, ...internal }: MonthOptionProps) => {
  const { value, label } = internal as MonthOptionInternalProps;
  return (
    <ListBoxItemRAC className={className} id={value} textValue={label}>
      {label}
    </ListBoxItemRAC>
  );
};

Month.Label = MonthLabel;
Month.Options = MonthOptions;
MonthOptions.Option = MonthOption;

type DayProps = {
  children: ReactNode;
  className?: string;
};

const Day = ({ children, className }: DayProps) => {
  const { disabled, showDateLabels } = useFieldPropsV2<DateTimeFieldProps>();
  const { partValue, partError, onPartChange } = useFieldPart(
    SupportedDateParts.DAY,
  );

  const labelElement = findChildOfType(children, Day.Label);
  const inputElement = findChildOfType(children, Day.Input);

  return (
    <div className={className} data-date-field={true}>
      {showDateLabels && labelElement}
      <TextFieldRAC
        value={partValue}
        onChange={onPartChange}
        isDisabled={disabled}
        isInvalid={partError}
      >
        {inputElement}
      </TextFieldRAC>
    </div>
  );
};

type DayLabelProps = {
  className?: string;
};

const DayLabel = ({ className }: DayLabelProps) => {
  return (
    <DateTimeLabel datePart={SupportedDateParts.DAY} className={className} />
  );
};

type DayInputProps = {
  className?: string;
};

const DayInput = ({ className }: DayInputProps) => {
  const { id, hidePlaceholder } = useFieldPropsV2<DateTimeFieldProps>();
  const { inputId } = useDateTimeFieldAttributes(SupportedDateParts.DAY);
  const { isFirstPart } = useFieldPart(SupportedDateParts.DAY);
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const placeholder = hidePlaceholder
    ? undefined
    : t('field.date.placeholder.day');

  useFocusFieldEvent(() => {
    if (isFirstPart) {
      inputRef.current?.focus();
    }
  }, id);

  return (
    <InputRAC
      ref={inputRef}
      id={inputId}
      className={className}
      placeholder={placeholder}
      maxLength={2}
      inputMode="numeric"
      data-hook={SupportedDateParts.DAY}
    />
  );
};

Day.Label = DayLabel;
Day.Input = DayInput;

type YearProps = {
  children: ReactNode;
  className?: string;
};

const Year = ({ children, className }: YearProps) => {
  const { disabled, showDateLabels } = useFieldPropsV2<DateTimeFieldProps>();
  const { partValue, partError, onPartChange } = useFieldPart(
    SupportedDateParts.YEAR,
  );

  const labelElement = findChildOfType(children, Year.Label);
  const inputElement = findChildOfType(children, Year.Input);

  return (
    <div className={className} data-date-field={true}>
      {showDateLabels && labelElement}
      <TextFieldRAC
        value={partValue}
        onChange={onPartChange}
        isDisabled={disabled}
        isInvalid={partError}
      >
        {inputElement}
      </TextFieldRAC>
    </div>
  );
};

type YearLabelProps = {
  className?: string;
};

const YearLabel = ({ className }: YearLabelProps) => {
  return (
    <DateTimeLabel datePart={SupportedDateParts.YEAR} className={className} />
  );
};

type YearInputProps = {
  className?: string;
};

const YearInput = ({ className }: YearInputProps) => {
  const { id, hidePlaceholder } = useFieldPropsV2<DateTimeFieldProps>();
  const { inputId } = useDateTimeFieldAttributes(SupportedDateParts.YEAR);
  const { isFirstPart } = useFieldPart(SupportedDateParts.YEAR);
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const placeholder = hidePlaceholder
    ? undefined
    : t('field.date.placeholder.year');

  useFocusFieldEvent(() => {
    if (isFirstPart) {
      inputRef.current?.focus();
    }
  }, id);

  return (
    <InputRAC
      ref={inputRef}
      id={inputId}
      className={className}
      placeholder={placeholder}
      maxLength={4}
      inputMode="numeric"
      data-hook={SupportedDateParts.YEAR}
    />
  );
};

Year.Label = YearLabel;
Year.Input = YearInput;

type TimeProps = {
  children: ReactNode;
  className?: string;
};

const Time = ({ children, className }: TimeProps) => {
  const { disabled, use24HourFormat, showDateLabels } =
    useFieldPropsV2<DateTimeFieldProps>();
  const { partValue, partError, onPartChange } = useFieldPart('time');

  const labelElement = findChildOfType(children, Time.Label);
  const inputElement = findChildOfType(children, Time.Input);

  return (
    <div className={className} data-date-field={true}>
      {showDateLabels && labelElement}
      <TimeFieldRAC
        value={partValue}
        onChange={onPartChange}
        isDisabled={disabled}
        isInvalid={partError}
        hourCycle={use24HourFormat ? 24 : 12}
      >
        {inputElement}
      </TimeFieldRAC>
    </div>
  );
};

type TimeLabelProps = {
  className?: string;
};

const TimeLabel = ({ className }: TimeLabelProps) => {
  const { inputId, labelId } = useDateTimeFieldAttributes('time');
  const { t } = useTranslation();

  const label = t('field.date.label.time');

  return (
    <label id={labelId} htmlFor={inputId} className={className}>
      {label}
    </label>
  );
};

type TimeInputProps = {
  children: ReactNode;
  className?: string;
};

const TimeInput = ({ children, className }: TimeInputProps) => {
  const hourElement = findChildOfType(children, Time.Hour);
  const minuteElement = findChildOfType(children, Time.Minute);
  const periodElement = findChildOfType(children, Time.Period);

  return (
    <DateInputRAC className={className}>
      {(segment) => {
        switch (segment.type) {
          case 'hour':
            return (
              applyInternalProps<HourInternalProps>(hourElement, {
                segment,
              }) || <></>
            );
          case 'minute':
            return (
              applyInternalProps<MinuteInternalProps>(minuteElement, {
                segment,
              }) || <></>
            );
          case 'dayPeriod':
            return (
              applyInternalProps<PeriodInternalProps>(periodElement, {
                segment,
              }) || <></>
            );
          case 'literal':
            return <DateSegmentRAC segment={segment} />;
          default:
            return <></>;
        }
      }}
    </DateInputRAC>
  );
};

type HourProps = {
  className?: string;
};

type HourInternalProps = {
  segment: DateSegment;
};

const Hour = ({ className, ...internal }: HourProps) => {
  const { hidePlaceholder } = useFieldPropsV2<DateTimeFieldProps>();
  const { segment } = internal as HourInternalProps;
  const { t } = useTranslation();

  const placeholder = hidePlaceholder
    ? undefined
    : t('field.date.placeholder.hour');

  return (
    <DateSegmentRAC
      segment={segment}
      className={className}
      {...(placeholder && { placeholder })}
    />
  );
};

type MinuteProps = {
  className?: string;
};

type MinuteInternalProps = {
  segment: DateSegment;
};

const Minute = ({ className, ...internal }: MinuteProps) => {
  const { hidePlaceholder } = useFieldPropsV2<DateTimeFieldProps>();
  const { segment } = internal as MinuteInternalProps;
  const { t } = useTranslation();

  const placeholder = hidePlaceholder
    ? undefined
    : t('field.date.placeholder.minute');

  return (
    <DateSegmentRAC
      segment={segment}
      className={className}
      {...(placeholder && { placeholder })}
    />
  );
};

type PeriodProps = {
  className?: string;
};

type PeriodInternalProps = {
  segment: DateSegment;
};

const Period = ({ className, ...internal }: PeriodProps) => {
  const { hidePlaceholder } = useFieldPropsV2<DateTimeFieldProps>();
  const { segment } = internal as PeriodInternalProps;
  const { t } = useTranslation();

  const placeholder = hidePlaceholder
    ? undefined
    : t('field.date.placeholder.period');

  return (
    <DateSegmentRAC
      segment={segment}
      className={className}
      {...(placeholder && { placeholder })}
    />
  );
};

Time.Label = TimeLabel;
Time.Input = TimeInput;
Time.Hour = Hour;
Time.Minute = Minute;
Time.Period = Period;

DateTimeInput.Label = Label;
DateTimeInput.Month = Month;
DateTimeInput.Day = Day;
DateTimeInput.Year = Year;
DateTimeInput.Time = Time;
DateTimeInput.Description = Description;
DateTimeInput.Error = Error;

function useDateTimeFieldAttributes(datePart: SupportedDateParts | 'time') {
  const { inputId } = useFieldAttributes();

  return useMemo(() => {
    const partName = datePart.toLowerCase();
    return {
      inputId: `${inputId}-${partName}-input`,
      labelId: `${inputId}-${partName}-label`,
    };
  }, [inputId, datePart]);
}

function useFieldPart(part: 'time'): {
  partValue: TimeValue | null;
  partError: boolean;
  onPartChange: (newValue: TimeValue | null) => void;
  isFirstPart: boolean;
};
function useFieldPart(part: SupportedDateParts): {
  partValue: string;
  partError: boolean;
  onPartChange: (newValue: string) => void;
  isFirstPart: boolean;
};
function useFieldPart(part: SupportedDateParts | 'time') {
  const {
    value: fieldValue,
    onChange: fieldOnChange,
    hasError = false,
  } = useFieldPropsV2<DateTimeFieldProps>();
  const { regionalFormat } = useConfig();

  const [dateValue, timeValue] = useMemo(
    () => parseDateTimeValue(fieldValue ?? ''),
    [fieldValue],
  );
  const errors = useMemo(
    () => getDateTimePartsErrors(hasError, fieldValue ?? ''),
    [fieldValue, hasError],
  );

  const dateValues = useMemo(() => deserializeDate(dateValue), [dateValue]);
  const parsedTimeValue = useMemo(
    () => stringToTimeValue(timeValue),
    [timeValue],
  );

  const onTimeChange = useCallback(
    (newValue: TimeValue | null) => {
      const stringValue = timeValueToString(newValue);
      const serializedValue = formatDateTimeValue(dateValue, stringValue);
      fieldOnChange(serializedValue);
    },
    [fieldOnChange, dateValue],
  );

  const onPartChange = useCallback(
    (newValue: string) => {
      const [, currentTime] = parseDateTimeValue(fieldValue ?? '');
      const serializedDate = serializeDate(
        newValue,
        part as SupportedDateParts,
        dateValues,
      );
      const serializedValue = formatDateTimeValue(serializedDate, currentTime);
      fieldOnChange(serializedValue);
    },
    [fieldOnChange, fieldValue, dateValues, part],
  );

  const isFirstPart = useMemo(() => {
    if (part === 'time') {
      return false;
    }
    const dateFormat = getDateFormatParts(regionalFormat)
      .filter(isSupportedDatePart)
      .map((p) => p.toUpperCase() as SupportedDateParts);
    const firstPart = dateFormat[0];
    return firstPart === part;
  }, [regionalFormat, part]);

  if (part === 'time') {
    return {
      partValue: parsedTimeValue,
      partError: errors.TIME,
      onPartChange: onTimeChange,
      isFirstPart: false,
    };
  }

  const partValue = dateValues[part] || '';

  return {
    partValue,
    partError: errors[part],
    onPartChange,
    isFirstPart,
  };
}

type DateTimeLabelProps = {
  datePart: SupportedDateParts;
  className?: string;
};

function DateTimeLabel({ datePart, className }: DateTimeLabelProps) {
  const { inputId, labelId } = useDateTimeFieldAttributes(datePart);
  const { t } = useTranslation();

  const translationKey = `field.date.label.${datePart.toLowerCase()}`;
  const label = t(translationKey);

  return (
    <label id={labelId} htmlFor={inputId} className={className}>
      {label}
    </label>
  );
}

function stringToTimeValue(value: string | null | undefined): TimeValue | null {
  if (!value) {
    return null;
  }
  try {
    return parseTime(value);
  } catch {
    return null;
  }
}

function timeValueToString(time: TimeValue | null): string | null {
  if (!time) {
    return null;
  }
  const hour = String(time.hour).padStart(2, '0');
  const minute = String(time.minute).padStart(2, '0');
  const second = String(time.second).padStart(2, '0');

  return [hour, minute, second].join(':');
}
